import React from 'react';
import { Helmet } from 'react-helmet';

import Link from '../../../utils/Link';
import { PrivacyCopies, handleFindPath } from '../../../constants/Copies';
import styles from '../styles/privacy.module.scss';

interface Props {
  routerProps: any;
  windowDimensions: { width: number; height: number };
  isTop: boolean;
  isMobileTablet: boolean;
  handleUpdateEntityData: (entityData: any) => void;
  handleResetEntityData: () => void;
  entityData: any;
}

export default class Privacy extends React.Component<Props> {
  componentDidMount() {
    this.props.handleResetEntityData();
  }

  render() {
    const { title1, title2 } = PrivacyCopies;

    return (
      <div className={styles.privacy}>
        <Helmet>
          <title>{PrivacyCopies.headTitle}</title>
        </Helmet>

        <div className={styles.privacy__inner}>
          <h1 className={styles.privacy__title}>
            {title1}
            <br />
            {title2}
          </h1>
          <div className={styles.privacy__body}>

		  <h4>Welcome to DocMatter!</h4> 

		  <p>
		  Our Mission is to support the clinicians who have dedicated their lives to providing care for others. 
		  We endeavor to make teaching, learning from, and collaborating with your peers as simple, efficient and impactful as possible. 
		  Central to this mission is our commitment to be transparent about the personal data we collect about you, how it is used, 
		  and with whom it is shared.
		  </p>

		  <h3>Privacy Policy</h3>
		  <p>Last Revision: August 15, 2025</p>

		  <p>
		  	This Privacy Policy applies to the processing of personal data by DocMatter Inc. ("DocMatter", "we," "us," or "our") 
		  	including on our website available at https://www.docmatter.com/, DocMatter-branded apps, and other DocMatter-related sites, 
		  	apps, communications, and other services that state that they are offered under this Privacy Policy ("Services").

		  </p>
		  <p>
		  	<b><u>Disclosure Regarding Sharing of Personal Data with Life Sciences Companies</u></b>. 
		  	Companies including pharmaceutical, biologic, and medical device innovators, manufacturers, 
		  	and distributors may view conversations shared using our Services.  Please note, pursuant to our Terms of Service, 
		  	such companies are not permitted to download, copy, share, or otherwise use or process such viewed conversations. 
		  	For additional information, please see the "How We Share Personal Data" section below.
		  </p>

		  <ol>
		  	<li>PERSONAL DATA WE COLLECT</li>
		  	<li>HOW WE USE PERSONAL DATA</li>
		  	<li>HOW WE SHARE PERSONAL DATA</li>
		  	<li>INTERNATIONAL USERS AND DATA TRANSFERS</li>
		  	<li>YOUR PRIVACY CHOICES AND RIGHTS</li>
		  	<li>CHILDREN'S INFORMATION</li>
		  	<li>DATA RETENTION</li>
		  	<li>SECURITY</li>
		  	<li>SITES AND SERVICES OF OTHERS</li>
		  	<li>SUPPLEMENTAL NOTICE FOR EU/UK GDPR</li>
		  	<li>CONTACT INFORMATION</li> 
		  </ol>

		  <h5><u>Changes to Our Privacy Policy</u></h5>

		  <p>
		  	We reserve the right, at our sole discretion, to change or modify portions of this Privacy Policy at any time. 
		  	If the Privacy Policy is changed, we will post the changes on this page and will indicate at the top of this page 
		  	the date this Privacy Policy was last revised. Any changes will become effective upon the date indicated. 
		  	If required by law, we will also notify you of any material changes to the Privacy Policy. 
		  	Your continued use of the Services after the date any such changes become effective constitutes your unequivocal 
		  	acceptance of, and agreement to, the new Privacy Policy (including any and all changes to the Privacy Policy).
		  </p>

		  <h4>1. Personal Data We Collect</h4>

		  <p>
		  	When you interact with us through the Services, we may collect personal data and other information from you, as further described below:
		  </p>

		  <h5><u>Personal Data That You Provide Through the Services</u></h5>

		  <p>
		  	We collect personal data from you when you voluntarily provide such information, such as when you contact us with inquiries, 
		  	register for access to the Services (thus becoming a "<u>Member</u>"), or use certain Services. 
		  </p>

		  <ul>
		  	<li><b>Account Information.</b> We may collect personal data when you register and use the Services. 
		  		The personal data you provide may include information such as your name, email address, physical address, 
		  		telephone number, and organization/institution. 
		  	</li>
		  	<li><b>Your Communications with Us.</b> We, and our service providers, may collect the information 
		  		you communicate to us, such as through email. 
		  	</li>
		  	<li><b>Interactive Features.</b> We and others who use our Services may collect personal data that you submit or 
		  		make available through our interactive features (e.g., messaging features, commenting functionalities, forums, blogs, 
		  		and social media pages). Any information you provide using any public sharing features of the Services will be 
		  		considered "public." 
		  	</li>
		  	<li><b>Conferences, Trade Shows, and Other Events.</b> We may collect personal data from individuals when we attend or 
		  		host conferences, trade shows, and other events. 
		  	</li>
		  	<li><b>Business Development and Strategic Partnerships.</b> We may collect personal data from individuals and third parties 
		  		to assess and pursue potential business opportunities. 
		  	</li>
		  	<li><b>Job Applications.</b> If you apply for a job with us, we will collect any personal data you provide in connection with 
		  		your application, such as your contact information and resume or CV. 
		  	</li> 
		  </ul>

		  <p>
		  	You have control over the information you share in your Member profile, as well as content you share through the Services. 
		  	If you share confidential information through the Services, there is no guarantee that information will stay confidential. 
		  	Please think about this before you share.
		  </p>


		  <h5><u>Personal Data from Others</u></h5>

		  <p>
		  	In addition to information we collect from you, we may receive personal data about you through publicly available 
		  	and other third-party sources, and through others' use of the Services, including other Members and DocMatter 
		  	partner members. For example, we may acquire clinician or other lists from DocMatter partners.
		  </p>

		  <h5><u>Personal Data Collected through Your Use of the Services</u></h5>

		  <p>
		  	When you interact with DocMatter through the Services, we may collect and store personal data about your device, 
		  	such as your Internet Protocol (IP) address, user settings, IMEI, MAC address, cookie identifiers, mobile carrier, 
		  	mobile device type, details about your browser, operating system or device, and other unique identifiers. 
		  </p>

		  <p>
		  	DocMatter may store such information itself or such information may be included in databases owned and maintained by 
		  	DocMatter agents or service providers. The Services may use such information and pool it with other information to 
		  	track use of the Services, including, for example, the total number of visitors, the number of visitors to each page, 
		  	and the domain names of our visitors' internet service providers. Doing this allows us to continuously improve 
		  	the Services for our Members. 
		  </p> 


		  <h5><u>Cookies, Pixels, and Other Technologies</u></h5>

		  <p>
		  	In operating the Services, we may use technologies that are operationally necessary, performance-related, 
		  	functionality-related, or Targeting-related to automatically collect information through the Services ("<u>Technology</u>" 
		  	or "<u>Technologies</u>"). Technologies are essentially small data files placed on your computer, tablet, mobile phone, 
		  	or other devices that allow us to record certain pieces of information whenever you visit or interact with our Services.
		  </p>

		  <p>
		  	One such Technology is called "cookies." A cookie is a piece of information that the computer that hosts our Services 
		  	gives to your browser when you access the Services. Our cookies help provide additional functionality to the Services, 
		  	help us analyze usage of the Services more accurately, and ensure you are able to stay logged in when you click 
		  	"Remember Me" during the sign-in process.
		  </p>

		  <p>
		  	You may stop or restrict the placement of Technologies on your device or remove them by adjusting your preferences as 
		  	your browser or device permits. On most web browsers, you will find a "help" section on the toolbar. 
		  	Please refer to this section for information on how to receive notification when you are receiving a new cookie 
		  	and how to turn cookies off. We recommend that you leave cookies turned on because they allow you to take advantage 
		  	of some of the features of the Services, such as enabling you to not have to log in each time you want to access the 
		  	Services from your personal computer's browser.
		  </p>

		  <p>
		  	We may also use a "pixel tag" (also known as a "web beacon"), which is a piece of code embedded within the Services 
		  	that collects information about users' engagement on the Services. The use of a pixel tag allows us to record, for example, 
		  	that a user has visited or clicked on a particular web page. We may also include web beacons in emails to understand 
		  	whether messages have been opened, acted on, or forwarded.
		  </p>

		  <p>
		  	We may collect personal data about your use of the Services, such as the pages that you visit, items that you search for, 
		  	the types of content you interact with, information about the links you click, the frequency and duration of your activities, 
		  	and other information about how you use the Services.
		  </p>

		  <p>
		  	We may also use MixPanel to collect information regarding visitor behavior and visitor demographics on some of our Services, 
		  	and to develop content. For more information about MixPanel, please visit https://mixpanel.com/legal/privacy-policy/. 
		  	You can opt out of MixPanel's collection and processing of data generated by your use of the Services by following 
		  	the procedure described at https://help.mixpanel.com/hc/en-us/articles/360001113426-Opt-Out-of-Tracking#do-not-track-settings.
		  </p>


		  <h5><u>Services Via Mobile Devices</u></h5>

		  <p>
		  	DocMatter provides Services that are specifically designed to be compatible and used on mobile devices. 
		  	DocMatter will collect certain information that your mobile device sends when you use such Services, 
		  	like a device identifier, user settings, location information, mobile carrier, and the operating system of your device. 
		  	Mobile versions of DocMatter's Services may require that users log in with an account. 
		  	In such cases, information about use of mobile versions of the Services may be associated with accounts. 
		  	In addition, DocMatter may enable individuals to download an application, widget, or other tool that can be used on 
		  	mobile or other computing devices. Some of these tools may store information on mobile or other devices and may 
		  	transmit personal data to DocMatter to enable individuals to access accounts and to enable DocMatter to track use of these 
		  	tools.
		  </p>

		  <h5><u>Protected Health Information and Other Patient Information</u></h5>

		  <p>
		  	DocMatter does not intentionally collect protected health information 
		  	(as such term is defined in the Health Insurance Portability and Accountability Act of 1996 and the rules promulgated thereunder ("<u>HIPAA</u>")), 
		  	personally identifiable information of patients, or other patient information. It is your responsibility to comply 
		  	with HIPAA and any other confidentiality obligations that apply to you. If you have reason to believe that 
		  	any such information is available through the Services, please contact us (contact information below).
		  </p>

		  <h4>2. How We Use Personal Data</h4>

		  <p>
		  	DocMatter uses personal data for a variety of business purposes consistent with applicable law and this Privacy Policy, 
		  	or as otherwise communicated to you at the point of collection.
		  </p>

		  <p><h5>A. Provide the Services</h5></p>

		  <p>
		  	We may use personal data to provide the Services, such as:  
		  </p>
		  <ul>
		  	<li>Managing Members and accounts;</li> 
		  	<li>Responding to questions, comments, and requests, and otherwise communicate with you;</li> 
		  	<li>Communicating with your colleagues or business contacts through our member referral Services;</li>
		  	<li>Providing access to certain areas, functionalities, and features of our Services;</li>
		  	<li>Sharing personal data with third parties as needed to provide the Services.</li>
		  </ul>


		  <p><h5>B. Operate Our Business</h5></p>

		  <p>
		  	We use personal data to operate our business, such as:
		  </p>

		  <ul>
		  	<li>Measuring interest in and engagement with the Services;</li>
		  	<li>Pursuing our legitimate interests such as conducting research and development, network and information security, and fraud prevention;</li> 
		  	<li>Preventing fraud or other potentially prohibited and/or illegal activities;</li>
		  	<li>Complying with laws;</li> 
		  	<li>Improving on and developing new Services;</li> 
		  	<li>Detecting and resolving bugs and other software issues;</li> 
		  	<li>Creating de-identified and/or aggregated information;</li>
		  	<li>Enforcing our Terms of Service and this Privacy Policy; and </li>
		  	<li>Providing you with notifications and customized materials. </li>
		  </ul>

		  <p><h5>C. Improve the Services and Develop New Products and Services</h5></p>

		  <p>
		  	We may also use personal data and other information collected through the Services to help us improve the content 
		  	and functionality of the Services and to develop new products and services, such as: 
		  </p>

		  <ul> 
		  	<li>Developing, training, and fine-tuning models, algorithms, and artificial intelligence technologies; and</li>
		  	<li>Improving, upgrading, or enhancing the Services.</li>
		  </ul>

		  <p><h5>D. With Your Consent or Direction</h5></p>

		  <p>
		  	DocMatter may use personal data (i) where you have consented, (ii) as otherwise directed by you, or 
		  	(iii) any other purpose disclosed to you (including through this Privacy Policy) at the time you provide personal data 
		  	or with your consent.
		  </p>


		  <h4>3. How We Share Personal Data</h4>

		  <p>
		  	DocMatter is not in the business of selling information. We consider information to be a vital part of our relationship 
		  	with you. There are, however, certain circumstances in which we may share personal data with certain third parties 
		  	without further notice to you, as set forth below:
		  </p>


		  <h5><u>Your Member Profile</u></h5>

		  <p>
		  	Any personal data included in your profile will be available to other Members. Your contact information 
		  	(email and phone number) will not be included in your profile, but we do include, for example, your institution, 
		  	profile photo, hospital affiliations, publications, etc. You can edit the information available in your Member profile.
		  </p>

		  <h5><u>Business Transfers</u></h5>

		  <p>
		  	As we develop our business, we may sell or buy businesses or assets. In the event of a corporate sale, merger, 
		  	acquisition, financing, reorganization, bankruptcy, receivership, purchase or sale of assets, transition of service 
		  	to another provider, dissolution, or similar event, personal data (public and nonpublic) may be part of the transferred 
		  	assets. In such event, DocMatter will endeavor to direct the transferee to use personal data in a manner 
		  	that is consistent with the Privacy Policy in effect at the time such personal data was collected.
		  </p>

		  <h5><u>Agents, Consultants, and Related Third Parties</u></h5>

		  <p>
		  	DocMatter, like many businesses, sometimes hires other companies to perform certain business-related functions and 
		  	we may share any personal data we collect with our third-party service providers for the purpose of maintaining and/or 
		  	improving the Services. Examples of such functions include mailing information, hosting services, customer service, 
		  	AI or machine learning services, IT support, and maintaining databases.
		  </p>

		  <h5><u>Other Users You Share or Interact With</u></h5>

		  <p>
		  	The Services may allow DocMatter users to share personal data or interact with other users of the Services.
		  </p>


		  <h5><u>Third-Party Services You Share or Interact With</u></h5>

		  <p>
		  	The Services may link to or allow you to interface with, interact with, share information with, 
		  	direct us to share information with, access, and/or use third-party websites, applications, services, products, 
		  	and technology (each a "<u>Third-Party Service</u>").
		  </p>

		  <p>
		  	Any personal data shared with a Third-Party Service will be subject to the Third-Party Service’s privacy policy. 
		  	We are not responsible for the processing of personal data by Third-Party Services.
		  </p>

		  <h5><u>Disclosures to Protect Us or Others</u></h5>

		  <p>
		  	We may access, preserve, and disclose personal data, other account information, and content if we believe doing so is 
		  	required or appropriate to: (i) comply with law enforcement or national security requests and/or legal process, 
		  	such as a court order or subpoena; (ii) respond to your requests; (iii) protect the rights, property, or safety of you, 
		  	DocMatter, or others; (iv) to enforce DocMatter policies or contracts; (v) to collect amounts owed to DocMatter; 
		  	(vi) when we believe disclosure is necessary or appropriate to prevent physical harm or financial loss, 
		  	or in connection with an investigation or prosecution of suspected or actual illegal activity; or 
		  	(vii) if we, in good faith, believe that disclosure is otherwise necessary or advisable.
		  </p>

		  <h4>4. International Users and Data Transfers</h4>

		  <p>
		  	By using the Services, DocMatter will transfer data to the United States. By choosing to utilize the Services or 
		  	otherwise providing information to us, you agree that any dispute over privacy or the terms contained in this 
		  	Privacy Policy will be governed by the laws of the United States, and the adjudication of any disputes arising 
		  	in connection with DocMatter or the Site will be in accordance with the <Link to={handleFindPath('TERMS')}>Terms of Service</Link>.
		  </p>

		  <p>
		  	If you are visiting from the European Union, Switzerland, the United Kingdom, or other regions with laws governing 
		  	data collection and use, please note that you are agreeing to the transfer of your information to the United States 
		  	and to processing of your data globally. By providing your personal data, you consent to any transfer and processing in 
		  	accordance with this Privacy Policy. If we transfer personal data from the European Economic Area, Switzerland, and/or 
		  	the United Kingdom to a country that does not provide an adequate level of protection under applicable data protection laws, 
		  	one of the safeguards we may use to support such transfer is the <a href="https://commission.europa.eu/law/law-topic/data-protection/international-dimension-data-protection/standard-contractual-clauses-scc_en">EU Standard Contractual Clauses</a>. 
		  </p>

		  <p>
		  	For more information about the safeguards we use for international transfers of your personal data, please contact us as set forth below.
		  </p>

		  <p>
		  	You agree that all personal data collected via or by DocMatter may be transferred, processed, and/or stored anywhere 
		  	in the world, including but not limited to, the United States, the European Union, in the cloud, on our servers, 
		  	or on the servers of our service providers. Your personal data may be accessible to law enforcement or other authorities 
		  	pursuant to a lawful request. By providing information to DocMatter, you explicitly consent to the storage of 
		  	your personal data in these locations.
		  </p>




		  <h4>5. Your Privacy Choices and Rights</h4>

		  <p>
		  	If you choose not to provide certain elements of personal data, you may not be able to use certain Services.
		  </p>

		  <h5><u>Your Privacy Rights</u></h5>

		  <p> 
		  	Consistent with applicable laws, you may have the right to request:
		  </p>

		  <ul>
		  	<li><b>Access to/Portability</b> of personal data about you consistent with legal requirements. 
		  		In addition, you may have the right in some cases to receive or have your electronic personal data transferred 
		  		to another party.
		  	</li>
		  	<li><b>Correction</b> of your personal data where it is inaccurate or incomplete. In some cases, 
		  		we may provide self-service tools that enable you to update your personal data.
		  	</li>
		  	<li><b>Deletion</b> of your personal data, subject to certain exceptions prescribed by law.
		  	</li>
		  	<li><b>Restriction of or object to processing</b> of your personal data, including the right to opt out of 
		  		the sale of your personal data to third parties, if applicable, where such requests are permitted by law.
		  	</li>
		  	<li><b>Opt Out of Certain Processing Activities</b>, including, as applicable, if we process your personal data 
		  		for "targeted advertising" (as "targeted advertising" is defined by applicable privacy laws), if we "sell" 
		  		your personal data (as "sell" is defined by applicable privacy laws), or if we engage in "profiling" 
		  		in furtherance of certain "decisions that produce legal or similarly significant effects" 
		  		concerning you (as such terms are defined by applicable privacy laws); and
		  	</li>
		  	<li><b>Withdrawal of Your Consent to Our Processing Your Personal Data.</b> Please note that your withdrawal will 
		  		take effect only for future processing and will not affect the lawfulness of processing before the withdrawal.
		  	</li>
		  </ul>

		  <p>
		  	If you would like to exercise any of these rights, please contact us at <a href="mailto:legal@docmatter.com">legal@docmatter.com</a>. 
		  	Please include your full name, email address associated with your account, and a detailed description of your data request. 
		  	We will process such requests in accordance with applicable laws. Although DocMatter makes good faith efforts 
		  	to honor individual requests with respect to personal data, there may be circumstances in which DocMatter is unable to do so, 
		  	including but not limited to: where the information contains legal privilege; would compromise others' 
		  	privacy or other legitimate rights; where the burden or expense would be disproportionate to the risks to 
		  	the individual's privacy in the case in question; or where it is commercially proprietary. 
		  	If DocMatter determines that your rights should be restricted in any particular instance, 
		  	we will provide you with an explanation of why that determination has been made and a contact point 
		  	for any further inquiries. To protect your privacy, DocMatter will take commercially reasonable steps 
		  	to verify your identity before fulfilling your request.
		  </p>

		  <h5><u>Your Privacy Choices</u></h5>

		  <p>
		  	<b>Email Communications.</b> If you receive an unwanted email from us, you may reply to the email requesting to opt out of receiving future emails. 
		  	We will process your request within a reasonable time after receipt. We may also send you certain non-promotional communications regarding DocMatter and our Services, 
		  	and you will not be able to opt out of those communications (e.g., communications regarding updates to our Terms or this Privacy Policy).
		  </p>

		  <p>
		  	<b>Mobile Devices.</b> We may occasionally send you push notifications through our mobile application with notices that may be of interest to you. 
		  	You may at any time opt out from receiving these types of communications by removing the mobile application from your mobile device or 
		  	by adjusting the settings on your device to prevent the app from pushing notices. We may also collect location-based information if you use our mobile application. 
		  	You may opt out of this collection by removing the mobile application from your mobile device or by adjusting the settings on your device to prevent the app 
		  	from collecting your location.
		  </p>

		  <p>
		  	<b>"Do Not Track."</b> Do Not Track ("<u>DNT</u>") is a privacy preference that users can set in certain web browsers. DNT is a way for users to inform websites 
		  	and services that they do not want certain information about their webpage visits collected over time and across websites or online services. 
		  	Please note that we do not respond to or honor DNT signals or similar mechanisms transmitted by web browsers. 
		  </p>

		  <p>
		  	<b>Cookies.</b> As noted above, you may stop or restrict the placement of cookies on your computer or remove them from your browser by adjusting your 
		  	web browser preferences. Please note that cookie-based opt-outs are not effective on mobile applications. However, on many mobile devices, application users 
		  	may opt out of certain mobile ads via their device settings by following the instructions for 
			<a href="https://support.google.com/googleplay/android-developer/answer/6048248?hl=en">Android</a>,{' '}
			<a href="https://support.apple.com/en-us/HT202074">iOS</a>, and <a href="https://www.networkadvertising.org/mobile-choice/">other mobile operating systems</a>.
		  </p>

		  <p>
		  	The online advertising industry also provides mechanisms that may allow you to opt out of receiving targeted ads from organizations that participate 
		  	in self-regulatory programs. To learn more, visit the <a href="http://www.networkadvertising.org/managing/opt_out.asp">Network Advertising Initiative</a>, 
		  	the <a href="https://youradchoices.com/control">Digital Advertising Alliance</a>, 
		  	and <a href="https://www.youronlinechoices.eu/">the European Digital Advertising Alliance</a>. 
		  </p>

		  <h4>6. Children's Information</h4>

		  <p>
		  	Our Services are not directed to children under 13 (or other age as required by local law), and we do not knowingly collect personal data from children. 
		  	In addition, you are not permitted to use our Services if you do not meet the minimum age requirement applicable to our Services in your jurisdiction. 
		  	If we learn that we have collected any personal data about a child in violation of applicable laws, we will promptly take steps to delete such information 
		  	and terminate the child's account.
		  </p>

		  <h4>7. Data Retention</h4>

		  <p>
		  	DocMatter retains the personal data we receive as described in this Privacy Policy for as long as you use our Services or as necessary to fulfill the purpose(s) 
		  	for which it was collected, provide our Services, resolve disputes, establish legal defenses, conduct audits, pursue legitimate business purposes, 
		  	enforce our agreements, and/or comply with applicable laws. Unless DocMatter is otherwise obligated to retain data, upon a Member's request that their account 
		  	be terminated and data deleted, DocMatter will endeavor to delete such Member's account and data.
		  </p>

		  <h4>8. Security</h4>

		  <p>
		  	DocMatter takes reasonable steps to protect the data (personal, public, non-public, and otherwise) provided via the Services from loss, misuse, 
		  	and unauthorized access, disclosure, alteration, or destruction. However, the Internet cannot be guaranteed to be 100% secure, and we cannot ensure 
		  	or warrant the security of any information you provide to us. We do not accept liability for any unintentional disclosures.
		  </p>
		  <p>
		  	By using the Services or by providing personal data to us, you agree that we may communicate with you electronically regarding security, privacy, and 
		  	administrative issues relating to your use of the Services. If we learn of a security system's breach, we may attempt to notify you electronically 
		  	by posting a notice on the Services or sending an email to you. You may have a legal right to receive this notice in writing.

		  </p>

		  <h4>9. Sites and Services of Others</h4>

		  <p>
		  	This Privacy Policy applies only to the Services. The Services may contain links to other websites, not operated or controlled by DocMatter. 
		  	The Privacy Policy does not apply to third-party websites. Any links from the Services to third party websites or content do not imply that 
		  	DocMatter endorses or has reviewed the third-party websites or content. We recommend contacting those third-party websites directly for information 
		  	on their privacy policies.
		  </p>

		  <h4>10. Supplemental Notice for EU/UK GDPR</h4>

		  <p>
		  	This Supplemental Notice for EU/UK GDPR applies only to our processing of personal data that is subject to the EU or UK General Data Protection Regulation.
		  </p>

		  <p>
		  	In some cases, providing personal information may be a requirement under applicable law, a contractual requirement, or a requirement necessary to enter into a contract. 
		  	If you choose not to provide personal information in cases where it is required, we will inform you of the consequences at the time of your refusal to provide 
		  	the personal information.
		  </p>

		  <p>
		  	DocMatter's processing of your personal information may be supported by one or more of the following lawful bases:
		  </p>

		  <div className={styles.table}>
		  	<div className={`${styles.tableRow} ${styles.header}`}>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>Privacy Policy Section</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>Lawful Basis: Performance of a Contract (i.e., to provide the Services to you)</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>Lawful Basis: Legitimate Interest</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>Lawful Basis: Consent</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>Lawful Basis: For Compliance with Legal Obligations</div>
		  	</div>

		  	<div className={styles.tableRow}>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>Section 2A: Provide the Services</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>&#10003;</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>&#10003;</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>&#10003;</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>&#10003;</div>
		  	</div>
		  	<div className={styles.tableRow}>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>Section 2B: Operate Our Business</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>&#10003;</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>&#10003;</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>&#10003;</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>&#10003;</div>
		  	</div>
		  	<div className={styles.tableRow}>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>Section 2C: Improve the Services and Develop New Products and Services</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>&#10003;</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>&#10003;</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>&#10003;</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>&#10003;</div>
		  	</div>
		  	<div className={styles.tableRow}>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>Section 2D: With Your Consent or Direction</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>&#10003;</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>&#10003;</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}>&#10003;</div>
		  		<div className={`${styles.tableColumn} ${styles.tableColumn1}`}></div>
		  	</div>
		  </div>

		  <p>
		  	If we process personal data that is considered a "special category of personal data," then our processing of this personal information may be supported 
		  	by one or more of the following conditions: 
		  </p>

		  <ul>
		  	<li><b>Explicit Consent</b>: You may have provided your explicit consent for our processing of your personal information. </li>
		  	<li><b>Necessary for Employment, Social Security, or Social Protection Law Purposes</b>: Our processing of your personal information may be necessary 
		  	for the purposes of carrying out obligations and exercising specific rights in the field of employment, social security, and/or social protection law. </li>
		  	<li><b>Necessary to Protect Vital Interests</b>: Our processing of your personal information may be necessary to protect your vital interests 
		  	if you are physically or legally incapable of giving consent. </li>
		  	<li><b>In Connection with a Foundation, Association, or Other Non-Profit Body</b>. Our processing of your personal information may be carried out 
		  	in the course of our legitimate activities in connection with our foundation, association, or other non-profit body.</li>
		  	<li><b>Publicly Available Personal Information</b>: Our processing of your personal information may relate to personal information that has been 
		  	manifestly made public by you. </li>
		  	<li><b>Necessary for the Establishment, Exercise, or Defense of Legal Claims</b>: Our processing of your personal information may be necessary 
		  	for the establishment, exercise, or defense of legal claims.</li>
		  	<li><b>Necessary for Substantial Public Interest</b>: Our processing of your personal information may be necessary for reasons of substantial public interest. </li>
		  	<li><b>Necessary for Medical Purposes</b>: Our processing of your personal information may be necessary for the purposes of preventive or occupational medicine, 
		  		medical diagnosis, the provision of health or social care or treatment, or the management of health or social care systems and services, 
		  	or it may be necessary pursuant to a contract with a health professional.</li> 
		  	<li><b>Necessary for Substantial Interest in the Area of Public Health</b>: Our processing of your personal information may be necessary for reasons 
		  	of public interest in the area of public health. </li>
		  	<li><b>Necessary for Archiving, Research, or Statistical Purposes</b>. Our processing of your personal information may be necessary for archiving purposes 
		  	in the public interest, scientific or historical research purposes, or statistical purposes.</li>
		  </ul>

		  <p>
		  	If you are located in the European Economic Area, you have the right to lodge a complaint with a supervisory authority if you believe our processing of 
		  	your personal data violates applicable law.
		  </p>

		  <h4>11. Contact Information</h4>

		  <p>
		  	Please feel free to contact us if you have any questions about DocMatter's Privacy Policy, or the information practices of the Services, 
		  	or for any purpose described above or otherwise, at (855) DOC-MATTER, <a href="mailto:legal@docmatter.com">legal@docmatter.com</a>, or via mail:
		  </p>
		  <div style={{ marginLeft: '20px' }}>
		  	DocMatter Privacy Policy Questions<br />
		  	460 Brannan Street #77452<br />
		  	San Francisco, CA 94107<br />
		  	USA <br />
		  </div>
		  <p>
		  	<br />
		  	To reach our Data Protection Officer, please call (855) DOC-MATTER, 
		  	email <a href="mailto:dataprotection@docmatter.com">dataprotection@docmatter.com</a>, or send correspondence to:
		  </p>
		  <div style={{ marginLeft: '20px' }}>
		  	Yong Lee, Ph.D<br />
		  	DocMatter Data Protection Officer<br />
		  	460 Brannan Street #77452<br />
		  	San Francisco, CA 94107<br />
		  	USA <br />
		  </div>
		</div>
	  </div>
    </div>
    );
  }
}
